import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {GroupEmptyPlacementType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupEmptyPlacementType'
import {IDatabaseGroupItem} from '@toolify/client/src/modules/databases/types/IDatabaseGroupItem'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {IGroupGranularityOptions} from '@toolify/server/src/services/DatabasePropertyService/types/IGroupGranularityOptions'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'

export const COMMON_GROUP_SORT_LABEL_KEYS: Record<GroupSortModeType, string> = {
  [GroupSortModeType.Natural]: 'database.header.groupSort.natural',
  [GroupSortModeType.ValueAsc]: 'database.header.groupSort.alphaAsc',
  [GroupSortModeType.ValueDesc]: 'database.header.groupSort.alphaDesc',
  [GroupSortModeType.CountAsc]: 'database.header.groupSort.countAsc',
  [GroupSortModeType.CountDesc]: 'database.header.groupSort.countDesc',
}

export interface IGroupSortDescriptor<TFullValue = unknown> {
  availableModes: GroupSortModeType[]
  defaultMode: GroupSortModeType
  getLabelKey: (mode: GroupSortModeType) => string
  compareGroupValues?: (a: TFullValue, b: TFullValue, direction: '1' | '-1') => number
}

export type {IGroupGranularityOptions}

export interface IGroupGranularityDescriptor {
  getDefault: () => GroupGranularityType
  getValues: () => GroupGranularityType[]
  getFullLabelKey: (value: string) => string
  getShortLabelKey: (value: string) => string
  icon?: IconNameType
  hasEditableOptions?: (granularity: GroupGranularityType) => boolean
  mapBucketFullValue?: (fullValue: unknown, granularity: GroupGranularityType, property: IDatabasePropertyResponseInstance | null | undefined) => unknown
  bucketValue?: (value: unknown, fullValue: unknown, granularity: GroupGranularityType, property: IDatabasePropertyResponseInstance | null | undefined, options?: IGroupGranularityOptions | null) => {value: unknown, fullValue: unknown} | null
}

export function resolveGroupGranularity(
  descriptor: IGroupGranularityDescriptor,
  value: unknown,
): GroupGranularityType {
  if(typeof value === 'string' && (descriptor.getValues() as string[]).includes(value)) {
    return value as GroupGranularityType
  }
  return descriptor.getDefault()
}

export interface IGroupSortStrategy<TFullValue = unknown> {
  groupSort: IGroupSortDescriptor<TFullValue>
  compareItemValues: (a: TFullValue, b: TFullValue, direction: '1' | '-1') => number
  sortGroupItems: (a: {fullValue: TFullValue}, b: {fullValue: TFullValue}) => number
}

export function resolveGroupSortMode(
  strategy: IGroupSortStrategy,
  storedMode: GroupSortModeType | undefined,
): GroupSortModeType {
  if (!storedMode || !strategy.groupSort.availableModes.includes(storedMode)) {
    return strategy.groupSort.defaultMode
  }
  return storedMode
}

export function getDefaultGroupEmptyPlacement(): GroupEmptyPlacementType {
  return GroupEmptyPlacementType.Bottom
}

export function buildGroupComparator(
  mode: GroupSortModeType,
  strategy: IGroupSortStrategy,
): (a: IDatabaseGroupItem, b: IDatabaseGroupItem) => number {
  switch (mode) {
  case GroupSortModeType.Natural:
    return (a, b) => strategy.sortGroupItems(a, b)
  case GroupSortModeType.ValueAsc: {
    const compare = strategy.groupSort.compareGroupValues ?? strategy.compareItemValues
    return (a, b) => compare(a.fullValue, b.fullValue, '1')
  }
  case GroupSortModeType.ValueDesc: {
    const compare = strategy.groupSort.compareGroupValues ?? strategy.compareItemValues
    return (a, b) => compare(a.fullValue, b.fullValue, '-1')
  }
  case GroupSortModeType.CountAsc:
    return (a, b) => (a.itemQuantity ?? 0) - (b.itemQuantity ?? 0)
  case GroupSortModeType.CountDesc:
    return (a, b) => (b.itemQuantity ?? 0) - (a.itemQuantity ?? 0)
  default: {
    const exhaustiveCheck: never = mode
    throw new Error(`Unhandled GroupSortModeType: ${String(exhaustiveCheck)}`)
  }
  }
}
